/**
 * CraveTheory product reviews — custom UI fed by Junip’s rendered widget.
 *
 * Approach:
 * 1. Mount the official `.junip-product-review` widget in a hidden host
 * 2. Wait for Junip to render into that host
 * 3. Scrape summary + reviews from the widget DOM into our frontend
 * 4. Write / see-more / sort proxy clicks into the hidden widget (no Display API)
 *
 * Demo reviews stay visible until the widget has real product reviews.
 */
(() => {
  const WIDGET_SRC = 'https://widgets.juniphq.com/v1/junip_shopify.js';
  const WIDGET_WAIT_MS = 12000;
  const WIDGET_POLL_MS = 160;

  const STAR_PATH =
    'M18.3039 8.97274L14.7882 12.0063L15.8593 16.5431C15.9184 16.7893 15.9032 17.0476 15.8156 17.2852C15.728 17.5229 15.5719 17.7292 15.3671 17.8782C15.1623 18.0272 14.9179 18.1121 14.6648 18.1222C14.4118 18.1324 14.1614 18.0673 13.9453 17.9352L9.99995 15.5071L6.0523 17.9352C5.83622 18.0666 5.58613 18.131 5.3335 18.1205C5.08087 18.11 4.837 18.0249 4.63261 17.8761C4.42822 17.7272 4.27243 17.5212 4.18488 17.284C4.09732 17.0468 4.08191 16.789 4.14058 16.5431L5.21558 12.0063L1.69995 8.97274C1.50878 8.80752 1.37052 8.58963 1.30244 8.34629C1.23436 8.10296 1.23947 7.84496 1.31715 7.60452C1.39483 7.36407 1.54162 7.15184 1.73919 6.99432C1.93677 6.83681 2.17637 6.74099 2.42808 6.71884L7.03745 6.34696L8.81558 2.04384C8.91182 1.80932 9.07563 1.60872 9.28618 1.46754C9.49673 1.32636 9.7445 1.25098 9.998 1.25098C10.2515 1.25098 10.4993 1.32636 10.7098 1.46754C10.9204 1.60872 11.0842 1.80932 11.1804 2.04384L12.9578 6.34696L17.5671 6.71884C17.8193 6.74017 18.0596 6.83545 18.2579 6.99275C18.4562 7.15005 18.6037 7.36236 18.6819 7.6031C18.76 7.84383 18.7654 8.10228 18.6973 8.34606C18.6292 8.58984 18.4907 8.80811 18.2992 8.97352L18.3039 8.97274Z';

  const STAR_OUTLINE =
    'M10.0002 14.7727L14.2752 17.4016C14.3861 17.4689 14.5145 17.5019 14.6441 17.4963C14.7738 17.4907 14.8988 17.4467 15.0035 17.3701C15.1082 17.2934 15.1878 17.1874 15.2323 17.0655C15.2767 16.9436 15.2841 16.8113 15.2533 16.6852L14.0908 11.7797L17.8955 8.49848C17.9925 8.41331 18.0625 8.30158 18.0967 8.17711C18.1309 8.05264 18.128 7.92087 18.0882 7.79806C18.0484 7.67526 17.9735 7.56679 17.8727 7.48607C17.772 7.40534 17.6498 7.35588 17.5213 7.34379L12.5283 6.93754L10.6049 2.28129C10.5559 2.16117 10.4721 2.05837 10.3645 1.98601C10.2568 1.91365 10.1299 1.875 10.0002 1.875C9.87045 1.875 9.74364 1.91365 9.63594 1.98601C9.52825 2.05837 9.44455 2.16117 9.39551 2.28129L7.47207 6.93754L2.4791 7.34379C2.3497 7.35516 2.22652 7.4044 2.12494 7.48537C2.02336 7.56634 1.9479 7.67544 1.90797 7.79905C1.86804 7.92267 1.86543 8.0553 1.90045 8.18039C1.93547 8.30548 2.00658 8.41748 2.10489 8.50239L5.90957 11.7836L4.74707 16.6852C4.71634 16.8113 4.72367 16.9436 4.76813 17.0655C4.8126 17.1874 4.8922 17.2934 4.99688 17.3701C5.10156 17.4467 5.22662 17.4907 5.35626 17.4963C5.48589 17.5019 5.61428 17.4689 5.7252 17.4016L10.0002 14.7727Z';

  /**
   * @param {HTMLElement} root
   */
  function init(root) {
    if (root.dataset.ctReviewsReady === 'true') return;
    root.dataset.ctReviewsReady = 'true';

    const cfg = readConfig(root);
    /** @type {{ mode: 'demo' | 'junip', reviews: any[], product: any, hasMore: boolean }} */
    const state = {
      mode: 'demo',
      reviews: JSON.parse(JSON.stringify(cfg.demo.reviews || [])),
      product: JSON.parse(JSON.stringify(cfg.demo.product || defaultDemoProduct())),
      hasMore: (cfg.demo.reviews || []).length > cfg.pageSize,
    };

    const refs = {
      average: root.querySelector('[data-ct-reviews-average]'),
      count: root.querySelector('[data-ct-reviews-count]'),
      stars: root.querySelector('[data-ct-reviews-summary-stars]'),
      bars: root.querySelector('[data-ct-reviews-bars]'),
      list: root.querySelector('[data-ct-reviews-list]'),
      more: root.querySelector('[data-ct-reviews-more]'),
      write: root.querySelector('[data-ct-reviews-write]'),
      filtersBtn: root.querySelector('[data-ct-reviews-filters-btn]'),
      filtersPanel: root.querySelector('[data-ct-reviews-filters-panel]'),
      sortBtn: root.querySelector('[data-ct-reviews-sort-btn]'),
      sortPanel: root.querySelector('[data-ct-reviews-sort-panel]'),
      sortLabel: root.querySelector('[data-ct-reviews-sort-label]'),
      status: root.querySelector('[data-ct-reviews-status]'),
      junipHost: root.querySelector('[data-ct-reviews-junip-host]'),
    };

    let sortKey = 'mostRecent';
    /** @type {Set<number>} */
    let ratingFilter = new Set();
    let mediaOnly = false;
    let visibleCount = cfg.pageSize;
    let scrapeBusy = false;
    /** @type {MutationObserver | null} */
    let hostObserver = null;

    ensureJunipMount();
    ensureJunipAssets(resolveStoreKey(cfg.storeKey));
    bindChrome();
    renderAll();
    hydrateFromJunipWidget();

    function ensureJunipMount() {
      if (!refs.junipHost || !cfg.productId) return;
      let mount = refs.junipHost.querySelector('.junip-product-review');
      if (!mount) {
        mount = document.createElement('span');
        mount.className = 'junip-product-review';
        refs.junipHost.appendChild(mount);
      }
      mount.setAttribute('data-product-id', cfg.productId);
      mount.setAttribute('data-sort-order', sortKey);
    }

    function bindChrome() {
      refs.write?.addEventListener('click', onWriteReview);
      refs.more?.addEventListener('click', onShowMore);

      refs.filtersBtn?.addEventListener('click', (event) => {
        event.stopPropagation();
        togglePanel('filters');
      });
      refs.sortBtn?.addEventListener('click', (event) => {
        event.stopPropagation();
        togglePanel('sort');
      });
      refs.filtersPanel?.addEventListener('click', (event) => event.stopPropagation());
      refs.sortPanel?.addEventListener('click', (event) => event.stopPropagation());

      root.querySelectorAll('[data-ct-reviews-sort-option]').forEach((btn) => {
        btn.addEventListener('click', () => {
          sortKey = btn.getAttribute('data-ct-reviews-sort-option') || 'mostRecent';
          if (refs.sortLabel) refs.sortLabel.textContent = btn.textContent?.trim() || 'Sort by';
          closePanels();
          if (state.mode === 'junip') applyJunipSort(sortKey);
          else {
            visibleCount = cfg.pageSize;
            renderList();
          }
        });
      });

      root.querySelectorAll('[data-ct-reviews-filter-rating]').forEach((input) => {
        input.addEventListener('change', () => {
          const rating = Number(input.getAttribute('data-ct-reviews-filter-rating'));
          if (input.checked) ratingFilter.add(rating);
          else ratingFilter.delete(rating);
          visibleCount = cfg.pageSize;
          if (state.mode === 'junip') applyJunipRatingFilter();
          else renderList();
        });
      });

      const mediaToggle = root.querySelector('[data-ct-reviews-filter-media]');
      mediaToggle?.addEventListener('change', () => {
        mediaOnly = Boolean(mediaToggle.checked);
        visibleCount = cfg.pageSize;
        if (state.mode === 'junip') {
          applyJunipMediaFilter();
        } else {
          renderList();
        }
      });

      document.addEventListener('click', () => closePanels());
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closePanels();
      });
    }

    function togglePanel(which) {
      const filtersOpen = refs.filtersPanel?.hasAttribute('data-open');
      const sortOpen = refs.sortPanel?.hasAttribute('data-open');
      closePanels();
      if (which === 'filters' && !filtersOpen) {
        refs.filtersPanel?.setAttribute('data-open', '');
        refs.filtersBtn?.setAttribute('aria-expanded', 'true');
      }
      if (which === 'sort' && !sortOpen) {
        refs.sortPanel?.setAttribute('data-open', '');
        refs.sortBtn?.setAttribute('aria-expanded', 'true');
      }
    }

    function closePanels() {
      refs.filtersPanel?.removeAttribute('data-open');
      refs.sortPanel?.removeAttribute('data-open');
      refs.filtersBtn?.setAttribute('aria-expanded', 'false');
      refs.sortBtn?.setAttribute('aria-expanded', 'false');
    }

    function renderAll() {
      renderSummary();
      renderList();
    }

    function renderSummary() {
      const product = state.product || cfg.demo.product;
      const average = Number(product.rating_average || 0);
      const count = Number(product.rating_count || 0);
      const dist = normalizeDistribution(product, state.reviews);

      if (refs.average) refs.average.textContent = average ? average.toFixed(1) : '0.0';
      if (refs.count) {
        refs.count.textContent = cfg.countTemplate.replace(/\[count\]|\{\{\s*count\s*\}\}/g, String(count));
      }
      if (refs.stars) refs.stars.innerHTML = starsMarkup(average, true);
      if (refs.bars) {
        refs.bars.innerHTML = [5, 4, 3, 2, 1]
          .map((star) => {
            const pct = dist[star] || 0;
            return `<div class="ct-reviews__bar-row" style="--pct: ${pct}">
              <span class="ct-reviews__bar-num">${star}</span>
              <span class="ct-reviews__bar-track" aria-hidden="true"><span class="ct-reviews__bar-fill"></span></span>
            </div>`;
          })
          .join('');
      }
    }

    function filteredReviews() {
      let list = [...state.reviews];
      if (ratingFilter.size) list = list.filter((r) => ratingFilter.has(Number(r.rating)));
      if (mediaOnly) list = list.filter((r) => (r.photos || []).length > 0);
      if (state.mode === 'demo') list.sort((a, b) => compareReviews(a, b, sortKey));
      return list;
    }

    function renderList() {
      if (!refs.list) return;
      const list = filteredReviews();
      const slice = state.mode === 'demo' ? list.slice(0, visibleCount) : list;

      if (!slice.length) {
        refs.list.innerHTML = `<p class="ct-reviews__empty">${escapeHtml(cfg.emptyText)}</p>`;
      } else {
        refs.list.innerHTML = slice.map((review, index) => reviewMarkup(review, index, cfg)).join('');
      }

      refs.list.querySelectorAll('[data-ct-reviews-photo]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const src = btn.getAttribute('data-ct-reviews-photo');
          if (src) window.open(src, '_blank', 'noopener,noreferrer');
        });
      });

      const canMore =
        state.mode === 'demo' ? visibleCount < list.length : Boolean(state.hasMore);
      if (refs.more) {
        refs.more.hidden = !canMore;
        refs.more.disabled = false;
      }

      if (refs.status) {
        refs.status.textContent =
          state.mode === 'junip'
            ? `Showing ${slice.length} Junip review${slice.length === 1 ? '' : 's'}`
            : 'Showing sample reviews';
      }
    }

    async function onShowMore() {
      if (state.mode === 'demo') {
        visibleCount += cfg.pageSize;
        renderList();
        return;
      }
      if (!refs.more || !refs.junipHost) return;
      refs.more.disabled = true;
      const before = refs.junipHost.querySelectorAll('.junip-review-list-item-container, .junip-review-list-item').length;
      const btn = findJunipSeeMore(refs.junipHost);
      if (!btn) {
        state.hasMore = false;
        refs.more.hidden = true;
        refs.more.disabled = false;
        return;
      }
      btn.click();
      const ok = await waitFor(
        () =>
          refs.junipHost.querySelectorAll('.junip-review-list-item-container, .junip-review-list-item')
            .length > before,
        6000
      );
      pullFromWidget();
      if (!ok) state.hasMore = Boolean(findJunipSeeMore(refs.junipHost));
      refs.more.disabled = false;
    }

    async function onWriteReview(event) {
      event.preventDefault();
      ensureJunipAssets(resolveStoreKey(cfg.storeKey));
      ensureJunipMount();

      const clicked = await clickJunipWrite(refs.junipHost, cfg.productId);
      if (clicked) return;
      if (cfg.writeReviewUrl) {
        window.open(cfg.writeReviewUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      announce(cfg.writeFallback);
    }

    async function hydrateFromJunipWidget() {
      if (!refs.junipHost || !cfg.productId) return;
      ensureJunipMount();
      ensureJunipAssets(resolveStoreKey(cfg.storeKey));

      const ready = await waitFor(() => isJunipReady(refs.junipHost), WIDGET_WAIT_MS);
      if (!ready) {
        console.info('[ct-reviews] Junip widget did not render — keeping demo reviews.');
        return;
      }

      const scraped = scrapeJunipHost(refs.junipHost);
      if (!scraped.reviews.length) {
        console.info('[ct-reviews] Junip widget empty — keeping demo reviews.');
        return;
      }

      state.mode = 'junip';
      state.product = scraped.product;
      state.reviews = scraped.reviews;
      state.hasMore = scraped.hasMore;
      renderAll();
      watchJunipHost();
    }

    function watchJunipHost() {
      if (!refs.junipHost || hostObserver) return;
      let timer = 0;
      hostObserver = new MutationObserver(() => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => {
          if (state.mode === 'junip') pullFromWidget();
        }, 180);
      });
      hostObserver.observe(refs.junipHost, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
      });
    }

    function pullFromWidget() {
      if (!refs.junipHost || scrapeBusy) return;
      scrapeBusy = true;
      try {
        const scraped = scrapeJunipHost(refs.junipHost);
        if (scraped.reviews.length) {
          state.mode = 'junip';
          state.product = scraped.product;
          state.reviews = scraped.reviews;
          state.hasMore = scraped.hasMore;
          renderAll();
        }
      } finally {
        scrapeBusy = false;
      }
    }

    function applyJunipSort(key) {
      if (!refs.junipHost) return;
      const select = findJunipSortSelect(refs.junipHost);
      if (select) {
        select.value = key;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        select.dispatchEvent(new Event('input', { bubbles: true }));
        waitFor(() => true, 80).then(() => pullFromWidget());
        return;
      }
      // Fallback: client-side sort of already scraped reviews
      state.reviews = [...state.reviews].sort((a, b) => compareReviews(a, b, key));
      renderList();
    }

    function applyJunipRatingFilter() {
      // Prefer client-side filter over scraped reviews (widget bar toggles are sticky/complex)
      renderList();
    }

    function applyJunipMediaFilter() {
      if (!refs.junipHost) {
        renderList();
        return;
      }
      const mediaInput = [...refs.junipHost.querySelectorAll('input[type="checkbox"]')].find((input) => {
        const label = (input.closest('label')?.textContent || input.getAttribute('aria-label') || '').toLowerCase();
        return /photo|media|image|with media/.test(label);
      });
      if (mediaInput && mediaInput.checked !== mediaOnly) {
        mediaInput.click();
        waitFor(() => true, 120).then(() => pullFromWidget());
        return;
      }
      renderList();
    }

    function announce(message) {
      if (!refs.status || !message) return;
      refs.status.textContent = message;
    }
  }

  function readConfig(root) {
    let demo = { product: defaultDemoProduct(), reviews: defaultDemoReviews() };
    try {
      const raw = root.querySelector('[data-ct-reviews-demo]')?.textContent;
      if (raw) demo = { ...demo, ...JSON.parse(raw) };
    } catch {
      /* keep defaults */
    }

    return {
      productId: root.getAttribute('data-product-id') || '',
      storeKey: root.getAttribute('data-store-key') || '',
      pageSize: Number(root.getAttribute('data-page-size') || 3) || 3,
      writeReviewUrl: root.getAttribute('data-write-url') || '',
      writeFallback:
        root.getAttribute('data-write-fallback') ||
        'Junip review form is loading — try again in a moment.',
      emptyText: root.getAttribute('data-empty-text') || 'No reviews match these filters yet.',
      countTemplate: root.getAttribute('data-count-template') || 'Based on [count] reviews',
      verifiedLabel: root.getAttribute('data-verified-label') || 'Verified buyer',
      demo,
    };
  }

  function resolveStoreKey(explicit) {
    if (explicit) return explicit;
    const el = document.querySelector('.junip-store-key[data-store-key], [data-store-key].junip-store-key');
    return el?.getAttribute('data-store-key') || el?.dataset?.storeKey || '';
  }

  function ensureJunipAssets(storeKey) {
    if (storeKey && !document.querySelector('.junip-store-key[data-store-key]')) {
      const span = document.createElement('span');
      span.className = 'junip-store-key';
      span.hidden = true;
      span.setAttribute('data-store-key', storeKey);
      document.body.appendChild(span);
    }
    if (!document.querySelector('script[src*="juniphq.com"]')) {
      const script = document.createElement('script');
      script.src = WIDGET_SRC;
      script.async = true;
      document.head.appendChild(script);
    }
  }

  function isJunipReady(host) {
    if (!host) return false;
    const mount = host.querySelector('.junip-product-review') || host;
    if (mount.classList.contains('junip-product-review-empty')) return true;
    if (mount.classList.contains('junip-product-review-rendered')) return true;
    if (host.querySelector('.junip-product-review-rendered')) return true;
    if (host.querySelector('.junip-review-list-item-container, .junip-review-list-item')) return true;
    if (host.querySelector('.junip-product-review-average, .junip-no-reviews')) return true;
    return false;
  }

  /**
   * @param {HTMLElement} host
   */
  function scrapeJunipHost(host) {
    const mount = host.querySelector('.junip-product-review') || host;
    const averageEl = mount.querySelector('.junip-product-review-average');
    const countEl = mount.querySelector('.junip-product-review-count');
    const average = parseFloat((averageEl?.textContent || '').replace(/[^\d.]/g, '')) || 0;
    const countMatch = (countEl?.textContent || '').match(/([\d,]+)/);
    const ratingCount = countMatch ? Number(countMatch[1].replace(/,/g, '')) : 0;

    const rating_distribution = {};
    mount.querySelectorAll('.junip-product-review-bar-wrapper, .junip-product-review-bars-container > *').forEach((row) => {
      const star = Number(row.querySelector('.junip-product-review-bar-rating')?.textContent?.trim());
      if (!(star >= 1 && star <= 5)) return;
      const width = row.querySelector('.junip-product-review-bar-filled')?.style?.width || '0%';
      const pct = parseFloat(width) || 0;
      // Convert percent of bar into relative weight; store as percent for our bars UI
      rating_distribution[star] = Math.round(pct);
    });

    // If bars only gave percentages, synthesize counts from percent * total for normalizeDistribution
    const distCounts = {};
    if (Object.keys(rating_distribution).length && ratingCount) {
      for (let i = 1; i <= 5; i += 1) {
        distCounts[i] = Math.round(((rating_distribution[i] || 0) / 100) * ratingCount);
      }
    }

    const itemNodes = mount.querySelectorAll('.junip-review-list-item-container');
    const nodes = itemNodes.length
      ? itemNodes
      : mount.querySelectorAll('.junip-review-list-item');

    const reviews = [...nodes]
      .map((node, index) => scrapeReviewNode(node, index))
      .filter((r) => r && (r.body || r.title || r.rating));

    return {
      product: {
        rating_average: average,
        rating_count: ratingCount || reviews.length,
        rating_distribution: Object.keys(distCounts).length ? distCounts : rating_distribution,
      },
      reviews,
      hasMore: Boolean(findJunipSeeMore(mount)),
    };
  }

  function scrapeReviewNode(node, index) {
    const name = cleanText(node.querySelector('.junip-customer-name')?.textContent) || 'Customer';
    const date = cleanText(node.querySelector('.junip-review-date')?.textContent);
    const title = cleanText(node.querySelector('.junip-review-title')?.textContent);
    const bodyEl = node.querySelector('.junip-review-body');
    let body = '';
    if (bodyEl) {
      const clone = bodyEl.cloneNode(true);
      clone.querySelectorAll('.junip-read-more').forEach((el) => el.remove());
      body = cleanText(clone.textContent);
    }
    const rating = scrapeStarRating(node);
    const verified = Boolean(
      node.querySelector('.junip-verified-badge, .junip-verified-container, [class*="junip-verified"]')
    );
    const photos = [...node.querySelectorAll('.junip-review-image')]
      .map((img) => img.getAttribute('src') || img.getAttribute('data-src') || '')
      .filter(Boolean);

    return {
      id: `junip-dom-${index}-${name}-${date}-${title}`.slice(0, 120),
      rating,
      title,
      body,
      created_at: date,
      customer_name: name,
      verified_buyer: verified,
      photos,
      up_vote_count: Number(cleanText(node.querySelector('.junip-vote-count')?.textContent)) || 0,
    };
  }

  function scrapeStarRating(node) {
    const filled = node.querySelector('.junip-stars-filled');
    if (filled) {
      const raw =
        filled.style.getPropertyValue('--junip-star-fill') ||
        getComputedStyle(filled).getPropertyValue('--junip-star-fill') ||
        '';
      const pct = parseFloat(raw);
      if (!Number.isNaN(pct) && pct > 0) return Math.max(1, Math.min(5, Math.round(pct / 20)));
    }
    const labeled = node.querySelector('[aria-label*="star" i], [aria-label*="rating" i]');
    const match = labeled?.getAttribute('aria-label')?.match(/(\d+(\.\d+)?)/);
    if (match) return Math.round(Number(match[1]));
    return 5;
  }

  function findJunipSeeMore(scope) {
    return (
      scope.querySelector('button.junip-see-more') ||
      [...scope.querySelectorAll('button')].find((btn) => /see more|load more|show more/i.test(btn.textContent || ''))
    );
  }

  function findJunipSortSelect(scope) {
    const selects = [...scope.querySelectorAll('select')];
    return (
      selects.find((select) =>
        [...select.options].some((opt) => /mostRecent|highestRated|oldest|mostHelpful/.test(opt.value))
      ) || null
    );
  }

  async function clickJunipWrite(host, productId) {
    const deadline = Date.now() + 5000;
    while (Date.now() < deadline) {
      const btn =
        host?.querySelector('.junip-write-review-cta') ||
        document.querySelector(
          `.junip-product-review[data-product-id="${productId}"] .junip-write-review-cta`
        ) ||
        document.querySelector('.junip-write-review-cta') ||
        findWriteButtonFallback(productId);
      if (btn instanceof HTMLElement) {
        btn.click();
        return true;
      }
      await wait(120);
    }
    return false;
  }

  function findWriteButtonFallback(productId) {
    const selectors = [
      `.junip-product-review[data-product-id="${productId}"] button`,
      '.junip-product-review button',
      '[class*="junip"][class*="write"]',
    ];
    for (const selector of selectors) {
      for (const node of document.querySelectorAll(selector)) {
        const text = (node.textContent || '').toLowerCase();
        if (
          node instanceof HTMLElement &&
          (text.includes('write') || node.className.toLowerCase().includes('write'))
        ) {
          return node;
        }
      }
    }
    return null;
  }

  function normalizeDistribution(product, reviews) {
    const raw = product?.rating_distribution;
    const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    let asPercent = false;

    if (raw && typeof raw === 'object') {
      for (let i = 1; i <= 5; i += 1) counts[i] = Number(raw[i] ?? raw[String(i)] ?? 0);
      const sum = Object.values(counts).reduce((a, b) => a + b, 0);
      // If values already look like percents totaling ~100, use directly
      if (sum > 0 && sum <= 105 && Object.values(counts).every((v) => v <= 100)) {
        const max = Math.max(...Object.values(counts));
        if (max <= 100 && sum >= 90) asPercent = true;
      }
    } else if (reviews?.length) {
      reviews.forEach((r) => {
        const rating = Math.round(Number(r.rating));
        if (counts[rating] !== undefined) counts[rating] += 1;
      });
    } else {
      return { 5: 80, 4: 14, 3: 4, 2: 1, 1: 1 };
    }

    if (asPercent) return counts;
    const total = Object.values(counts).reduce((a, b) => a + b, 0) || 1;
    return {
      5: Math.round((counts[5] / total) * 100),
      4: Math.round((counts[4] / total) * 100),
      3: Math.round((counts[3] / total) * 100),
      2: Math.round((counts[2] / total) * 100),
      1: Math.round((counts[1] / total) * 100),
    };
  }

  function compareReviews(a, b, sortKey) {
    switch (sortKey) {
      case 'oldest':
        return String(a.created_at).localeCompare(String(b.created_at));
      case 'highestRated':
        return b.rating - a.rating;
      case 'lowestRated':
        return a.rating - b.rating;
      case 'mostHelpful':
        return (b.up_vote_count || 0) - (a.up_vote_count || 0);
      case 'mostRecent':
      default:
        return String(b.created_at).localeCompare(String(a.created_at));
    }
  }

  function starsMarkup(rating, summary = false) {
    const value = Number(rating) || 0;
    const full = Math.floor(value);
    const items = [];
    for (let i = 1; i <= 5; i += 1) {
      if (i <= full) {
        items.push(
          `<span class="ct-reviews__rate is-filled" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="${STAR_PATH}" fill="currentColor"/></svg></span>`
        );
      } else {
        items.push(
          `<span class="ct-reviews__rate" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="${STAR_OUTLINE}" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/></svg></span>`
        );
      }
    }
    const label = summary ? `${value.toFixed(1)} out of 5 stars` : `${full} out of 5 stars`;
    return `<div class="ct-reviews__stars" role="img" aria-label="${label}">${items.join('')}</div>`;
  }

  function reviewMarkup(review, index, cfg) {
    const photos = review.photos || [];
    const date = formatDate(review.created_at);
    const verified = review.verified_buyer
      ? `<span class="ct-reviews__verified">${escapeHtml(cfg.verifiedLabel)}<svg class="ct-reviews__verified-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M14.1163 6.42625C13.8806 6.18 13.6369 5.92625 13.545 5.70312C13.46 5.49875 13.455 5.16 13.45 4.83187C13.4406 4.22187 13.4306 3.53062 12.95 3.05C12.4694 2.56937 11.7781 2.55937 11.1681 2.55C10.84 2.545 10.5013 2.54 10.2969 2.455C10.0744 2.36312 9.82 2.11937 9.57375 1.88375C9.1425 1.46937 8.6525 1 8 1C7.3475 1 6.85812 1.46937 6.42625 1.88375C6.18 2.11937 5.92625 2.36312 5.70312 2.455C5.5 2.54 5.16 2.545 4.83187 2.55C4.22187 2.55937 3.53062 2.56937 3.05 3.05C2.56937 3.53062 2.5625 4.22187 2.55 4.83187C2.545 5.16 2.54 5.49875 2.455 5.70312C2.36312 5.92562 2.11937 6.18 1.88375 6.42625C1.46937 6.8575 1 7.3475 1 8C1 8.6525 1.46937 9.14187 1.88375 9.57375C2.11937 9.82 2.36312 10.0737 2.455 10.2969C2.54 10.5013 2.545 10.84 2.55 11.1681C2.55937 11.7781 2.56937 12.4694 3.05 12.95C3.53062 13.4306 4.22187 13.4406 4.83187 13.45C5.16 13.455 5.49875 13.46 5.70312 13.545C5.92562 13.6369 6.18 13.8806 6.42625 14.1163C6.8575 14.5306 7.3475 15 8 15C8.6525 15 9.14187 14.5306 9.57375 14.1163C9.82 13.8806 10.0737 13.6369 10.2969 13.545C10.5013 13.46 10.84 13.455 11.1681 13.45C11.7781 13.4406 12.4694 13.4306 12.95 12.95C13.4306 12.4694 13.4406 11.7781 13.45 11.1681C13.455 10.84 13.46 10.5013 13.545 10.2969C13.6369 10.0744 13.8806 9.82 14.1163 9.57375C14.5306 9.1425 15 8.6525 15 8C15 7.3475 14.5306 6.85812 14.1163 6.42625ZM10.8538 6.85375L7.35375 10.3538C7.30731 10.4002 7.25217 10.4371 7.19147 10.4623C7.13077 10.4874 7.06571 10.5004 7 10.5004C6.93429 10.5004 6.86923 10.4874 6.80853 10.4623C6.74783 10.4371 6.69269 10.4002 6.64625 10.3538L5.14625 8.85375C5.05243 8.75993 4.99972 8.63268 4.99972 8.5C4.99972 8.36732 5.05243 8.24007 5.14625 8.14625C5.24007 8.05243 5.36732 7.99972 5.5 7.99972C5.63268 7.99972 5.75993 8.05243 5.85375 8.14625L7 9.29313L10.1462 6.14625C10.1927 6.09979 10.2479 6.06294 10.3086 6.0378C10.3692 6.01266 10.4343 5.99972 10.5 5.99972C10.5657 5.99972 10.6308 6.01266 10.6914 6.0378C10.7521 6.06294 10.8073 6.09979 10.8538 6.14625C10.9002 6.1927 10.9371 6.24786 10.9622 6.30855C10.9873 6.36925 11.0003 6.4343 11.0003 6.5C11.0003 6.5657 10.9873 6.63075 10.9622 6.69145C10.9371 6.75214 10.9002 6.8073 10.8538 6.85375Z" fill="currentColor"/></svg></span>`
      : '';

    const photosHtml = photos.length
      ? `<div class="ct-reviews__photos">${photos
          .slice(0, 4)
          .map(
            (src, i) =>
              `<button type="button" class="ct-reviews__photo" data-ct-reviews-photo="${escapeAttr(src)}" aria-label="Open review photo ${i + 1}"><img src="${escapeAttr(src)}" alt="" width="120" height="120" loading="lazy" decoding="async"></button>`
          )
          .join('')}</div>`
      : '';

    return `<article class="ct-reviews__item" data-ct-reveal="up" style="--ct-delay: ${120 + index * 60}ms">
      <div class="ct-reviews__meta">
        <div class="ct-reviews__who">
          <p class="ct-reviews__name">${escapeHtml(review.customer_name)}</p>
          ${verified}
        </div>
        ${date ? `<p class="ct-reviews__date">${escapeHtml(date)}</p>` : ''}
      </div>
      <div class="ct-reviews__content">
        <div class="ct-reviews__headline">
          ${starsMarkup(review.rating)}
          ${review.title ? `<h3 class="ct-reviews__title">${escapeHtml(review.title)}</h3>` : ''}
        </div>
        ${review.body ? `<p class="ct-reviews__body">${escapeHtml(review.body)}</p>` : ''}
        ${photosHtml}
      </div>
    </article>`;
  }

  function formatDate(value) {
    if (!value) return '';
    // Junip widget often already shows relative dates ("2 months ago") — keep as-is
    if (/ago|yesterday|today|hour|minute|day|week|month|year/i.test(String(value))) {
      return String(value).trim();
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }

  function cleanText(value) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escapeAttr(value) {
    return escapeHtml(value).replace(/`/g, '');
  }

  function wait(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  function waitFor(predicate, timeoutMs) {
    const start = Date.now();
    return new Promise((resolve) => {
      const tick = () => {
        if (predicate()) return resolve(true);
        if (Date.now() - start >= timeoutMs) return resolve(false);
        window.setTimeout(tick, WIDGET_POLL_MS);
      };
      tick();
    });
  }

  function defaultDemoProduct() {
    return {
      rating_average: 4.8,
      rating_count: 245,
      rating_distribution: { 5: 196, 4: 34, 3: 10, 2: 3, 1: 2 },
    };
  }

  function defaultDemoReviews() {
    return [
      {
        id: 'demo-1',
        rating: 5,
        title: 'Calm without drowsiness',
        body: 'I take it in the afternoon when stress builds up. It helps me stay calm and focused without making me sleepy.',
        created_at: '2025-02-27',
        customer_name: 'John A.',
        verified_buyer: true,
        photos: [],
      },
      {
        id: 'demo-2',
        rating: 4,
        title: 'Perfect for daily use',
        body: "I like that it doesn't feel overwhelming. Easy to take and fits into my routine.",
        created_at: '2025-02-27',
        customer_name: 'Sofia L.',
        verified_buyer: true,
        photos: [],
      },
      {
        id: 'demo-3',
        rating: 5,
        title: 'Calm without drowsiness',
        body: 'I take it in the afternoon when stress builds up. It helps me stay calm and focused without making me sleepy.',
        created_at: '2025-02-27',
        customer_name: 'John A.',
        verified_buyer: true,
        photos: [],
      },
    ];
  }

  function boot() {
    document.querySelectorAll('[data-ct-product-reviews]').forEach((root) => {
      if (root instanceof HTMLElement) init(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target?.querySelector?.('[data-ct-product-reviews]');
    if (root instanceof HTMLElement) {
      root.dataset.ctReviewsReady = 'false';
      init(root);
    }
  });
})();
